import {
  useExternalContractsControllerGetSettlements,
  type SupplierSettlementResponseDto,
} from "@semo/api-contracts";
import { useMemo } from "react";

/**
 * @param previewData DEV 전용: 값이 있으면 API 대신 이 더미 데이터로 렌더링한다
 *                    (/dev/preview?tab=settlements). API 호출은 비활성화된다.
 */
export function useSettlements(previewData?: SupplierSettlementResponseDto[]) {
  const { data, isLoading, error, refetch } =
    useExternalContractsControllerGetSettlements({
      query: { enabled: !previewData },
    });

  const settlements = useMemo(
    () => previewData ?? data?.data ?? [],
    [previewData, data],
  );

  /**
   * 통계는 **수수료 축**이다 — 세모에 낼 돈.
   *
   * 종전에는 `settlementAmount`(계약금액 − 수수료 = 세모가 공급사에 보낼 대금)를 합산했는데,
   * 계약 대금이 직거래로 바뀌면서 그 금액은 아무도 보내지 않는 숫자가 됐다. 합산 축을
   * `commissionAmount` 로 옮긴다.
   */
  const stats = useMemo(() => {
    const totalCount = settlements.length;
    const pendingCount = settlements.filter(
      (s) => s.settlementStatus === "PENDING",
    ).length;
    const completedCount = settlements.filter(
      (s) => s.settlementStatus === "COMPLETED",
    ).length;
    const waitingCount = settlements.filter(
      (s) => s.settlementStatus === "WAITING",
    ).length;

    const totalCommission = settlements.reduce(
      (sum, s) => sum + s.commissionAmount,
      0,
    );
    const collectedCommission = settlements
      .filter((s) => s.settlementStatus === "COMPLETED")
      .reduce((sum, s) => sum + s.commissionAmount, 0);
    // 확정 대기(WAITING)도 아직 안 낸 돈이라 미수에 함께 센다.
    const unpaidCommission = settlements
      .filter(
        (s) =>
          s.settlementStatus === "PENDING" || s.settlementStatus === "WAITING",
      )
      .reduce((sum, s) => sum + s.commissionAmount, 0);

    return {
      totalCount,
      pendingCount,
      completedCount,
      waitingCount,
      totalCommission,
      collectedCommission,
      unpaidCommission,
    };
  }, [settlements]);

  return {
    settlements,
    isLoading: previewData ? false : isLoading,
    error,
    stats,
    refetch,
  };
}
